/**
 * Versioned composite JSON contracts for generated game assets.
 *
 * These contracts are shared by:
 *   - the asset generator (produces files conforming to these shapes)
 *   - the frontend (reads them to configure renderers / tilemap parsers)
 *   - LLM prompts (the generator prompt declares these exact shapes)
 *   - tests (type-guards validate generated fixtures)
 *
 * Frame-name convention: `<slug>:<index>` (e.g. `player-walk:0`).
 * The `frames` record keys and the `order` / `frames` arrays in any
 * consuming atlas MUST use this exact format.
 */

// ── Animation Atlas ──────────────────────────────────────────────────────────

/**
 * Animation atlas descriptor.
 * Lives at `data/atlas/<slug>.json` in the sandbox assets tree.
 *
 * `frames` keys follow the `<slug>:<index>` convention (e.g. `player-walk:0`).
 * `order` lists those keys in playback order; its length equals the number of
 * frames rendered into the sprite sheet.
 */
export interface AnimationAtlas {
  version: 1;
  type: 'animation-atlas';
  /** Assets-root-relative path to the sprite-sheet image, e.g. `images/sheets/player-walk.png`. */
  image: string;
  frameWidth: number;
  frameHeight: number;
  /** Map of frame name → pixel rect within the sprite sheet. */
  frames: Record<string, { x: number; y: number; w: number; h: number }>;
  /** Frame names in playback order (must be keys of `frames`). */
  order: string[];
  /** Playback speed in frames per second. */
  fps?: number;
}

/**
 * Type guard for {@link AnimationAtlas}.
 * Structural check on version/type discriminant + required field types.
 * Tolerant of extra fields present in the object.
 */
export function isAnimationAtlas(value: unknown): value is AnimationAtlas {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v['version'] === 1 &&
    v['type'] === 'animation-atlas' &&
    typeof v['image'] === 'string' &&
    typeof v['frameWidth'] === 'number' &&
    typeof v['frameHeight'] === 'number' &&
    typeof v['frames'] === 'object' && v['frames'] !== null && !Array.isArray(v['frames']) &&
    Array.isArray(v['order']) && (v['order'] as unknown[]).every(f => typeof f === 'string')
  );
}

// ── Tilemap ──────────────────────────────────────────────────────────────────

/**
 * Tilemap descriptor following Tiled conventions.
 * Lives at `data/tilemaps/<slug>.json` in the sandbox assets tree.
 *
 * Layer data is row-major; `0` = empty cell; tile ids are 1-based (matching
 * the Tiled "firstgid" convention where id 1 = first tile in the tileset).
 */
export interface Tilemap {
  version: 1;
  type: 'tilemap';
  /** Assets-root-relative path to the tileset image, e.g. `images/tilesets/dungeon.png`. */
  image: string;
  tileWidth: number;
  tileHeight: number;
  /** Number of tile columns in the tileset image. */
  columns: number;
  /** Total number of tiles in the tileset. */
  tileCount: number;
  /** Map width in tiles. */
  width: number;
  /** Map height in tiles. */
  height: number;
  layers: Array<{
    name: string;
    /** Row-major flat array of tile ids (0 = empty, 1-based tile ids). */
    data: number[];
  }>;
}

/**
 * Type guard for {@link Tilemap}.
 * Structural check on version/type discriminant + required field types.
 * Tolerant of extra fields present in the object.
 */
export function isTilemap(value: unknown): value is Tilemap {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v['version'] === 1 &&
    v['type'] === 'tilemap' &&
    typeof v['image'] === 'string' &&
    typeof v['tileWidth'] === 'number' &&
    typeof v['tileHeight'] === 'number' &&
    typeof v['columns'] === 'number' &&
    typeof v['tileCount'] === 'number' &&
    typeof v['width'] === 'number' &&
    typeof v['height'] === 'number' &&
    Array.isArray(v['layers'])
  );
}
